import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { CommandCategoryIcon, DangerLevelBadge } from '../../../components/commands';
import { COMMAND_PERMISSIONS, canExecuteDangerLevel } from '../../../constants/Permissions';
import { Command } from '../../../types/commands';
import { CommandExecutionForm } from './CommandExecutionForm';

interface CommandListProps {
  unitId: number;
  canExecute: boolean;
  /** Optional unit hostname for command feedback */
  unitHostname?: string;
}

/**
 * List of available commands for a unit
 * Grouped by category with execute buttons
 */
export const CommandList: React.FC<CommandListProps> = ({ unitId, canExecute, unitHostname }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const [commands, setCommands] = useState<Command[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  useEffect(() => {
    loadCommands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  const loadCommands = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await dataProvider.getUnitCommands(unitId);
      setCommands(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load commands');
      notify('Failed to load commands', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user can execute a specific command based on danger level
   */
  const canExecuteCommand = (command: Command): boolean => {
    if (!canExecute) return false;
    if (!command.is_active) return false;
    return canExecuteDangerLevel(permissions, command.danger_level);
  };

  /**
   * Get disabled reason for a command
   */
  const getDisabledReason = (command: Command): string | null => {
    if (!command.is_active) return 'Command is not active';
    if (!canExecute) return "You don't have permission to execute commands";

    if (command.danger_level === 'critical' && !permissions?.includes(COMMAND_PERMISSIONS.EXECUTE_CRITICAL_COMMANDS)) {
      return "You don't have permission to execute critical commands";
    }

    if (command.danger_level === 'high' && !permissions?.includes(COMMAND_PERMISSIONS.EXECUTE_HIGH_RISK_COMMANDS)) {
      return "You don't have permission to execute high-risk commands";
    }

    return null;
  };

  // Group commands by category
  const groupedCommands = commands.reduce(
    (acc, cmd) => {
      if (!acc[cmd.category]) {
        acc[cmd.category] = [];
      }
      acc[cmd.category].push(cmd);
      return acc;
    },
    {} as Record<string, Command[]>,
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  if (commands.length === 0) {
    return <Alert severity="info">No commands available for this unit.</Alert>;
  }

  return (
    <>
      {Object.entries(groupedCommands).map(([category, cmds]) => (
        <Box key={category} mb={4}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CommandCategoryIcon category={category as any} />
            {category.charAt(0).toUpperCase() + category.slice(1)} Commands
          </Typography>

          <Grid container spacing={2}>
            {cmds.map((command) => (
              <Grid item xs={12} sm={6} md={4} key={command.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="h6" component="div">
                        {command.display_name || command.name}
                      </Typography>
                      <DangerLevelBadge level={command.danger_level} size="small" />
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {command.description}
                    </Typography>

                    {command.requires_parameters && <Chip label="Has parameters" size="small" sx={{ mt: 1 }} />}
                  </CardContent>

                  <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                    <Tooltip title={getDisabledReason(command) || ''} arrow>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setSelectedCommand(command)}
                          disabled={!canExecuteCommand(command)}
                          fullWidth
                        >
                          Execute
                        </Button>
                      </span>
                    </Tooltip>

                    {getDisabledReason(command) && (
                      <Typography variant="caption" color="error" textAlign="center">
                        {getDisabledReason(command)}
                      </Typography>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {selectedCommand && (
        <CommandExecutionForm
          command={selectedCommand}
          unitId={unitId}
          unitHostname={unitHostname}
          open={!!selectedCommand}
          onClose={() => setSelectedCommand(null)}
          onSuccess={() => {
            setSelectedCommand(null);
            notify('Command executed successfully', { type: 'success' });
          }}
        />
      )}
    </>
  );
};
